/**
 * In-memory Ed25519 message signer for E2E (`__SOLOMON_E2E__`) runs only.
 *
 * This test signer is imported from `./provider` and only exercised when
 * `window.__SOLOMON_E2E__.signer` is set — never in a real user session.
 *
 * It implements `signMessage` (the only method the YaaS wallet-verification
 * ceremonies call) for real, so a payload-bound Ed25519 signature can be
 * produced without a browser wallet extension. The transaction-signing
 * methods are deliberate throw-stubs: the retail signature ceremonies are
 * signMessage-only, so any path that reaches a transaction signer under the
 * smoke harness is a bug and should fail loudly rather than silently.
 */

import { signAsync } from "@noble/ed25519";
import { Base58Util } from "@solomon-labs/base58";

import type { SolomonE2ESignerOverride } from "./types";

export interface SmokeSigner {
    /** Base58 wallet address this signer signs as. Mirrors the real
     *  `useTransactionSigner().address`. */
    address: string;
    /** Real Ed25519 detached signature over `message`. */
    signMessage(message: Uint8Array): Promise<Uint8Array>;
    /** Smoke harness is signMessage-only — fail loudly on any tx path. */
    signTransaction(): never;
    signAndSendTransaction(): never;
}

export function createSmokeSigner(override: SolomonE2ESignerOverride): SmokeSigner {
    // A Solana keypair secret is 64 bytes (32-byte seed ‖ 32-byte public
    // key); @noble/ed25519 wants the 32-byte seed only. Cast to the
    // decoder's branded input type — the override string is test-fixture
    // data, validated by producing a real signature downstream.
    const seed = Base58Util.decode(
        override.secretKeyBase58 as unknown as Parameters<typeof Base58Util.decode>[0]
    ).slice(0, 32);

    const fail = (method: string): never => {
        throw new Error(`[e2e-signer] ${method} is not supported — the smoke signer is signMessage-only.`);
    };

    return {
        address: override.publicKeyBase58,
        // Async variant: noble's sync `sign` throws unless `hashes.sha512`
        // is set; `signAsync` uses WebCrypto with no setup. The verify-path
        // call site (`useWalletVerification`) already awaits signMessage.
        signMessage: (message) => signAsync(message, seed),
        signTransaction: () => fail("signTransaction"),
        signAndSendTransaction: () => fail("signAndSendTransaction")
    };
}
